import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { getCatalog, getCategoryBySlugOrId, getCategoryPage, getSubcategoryBySlugOrId } from '~/entities/catalog/api';
import { withGlobalSearch } from '~/entities/catalog/url-search';
import { SubcategoryDetailPage } from '~/pages/subcategory-detail';
// By path, not through the page's index: the loader stays in the main chunk (only the component is split off).
import { subcategoryPageData } from '~/pages/subcategory-detail/model/page-data';

const searchSchema = withGlobalSearch({
  sub: z.string().optional(),
  item: z.string().optional(),
  child: z.string().optional(),
  q: z.string().optional(),
  connection: z.string().optional(),
  location: z.string().optional(),
  color: z.string().optional(),
  panel: z.string().optional(),
  availability: z.string().optional(),
  attrs: z.string().optional(),
});

export const Route = createFileRoute('/categories_/$categoryId/$subcategoryId')({
  validateSearch: searchSchema,
  // Returns only what the page renders (SubcategoryPageData), never the whole catalog, and reads no search
  // params for it: `?sub=`, `?q=` and the filters narrow the list in the component, so the query doesn't change
  // the data a server render would serialise. getCatalog() stays awaited: a runtime `catalogUrl` fetches it.
  loader: async ({ params, location }) => {
    const catalog = await getCatalog();
    const category = await getCategoryBySlugOrId(params.categoryId);
    const page = await getSubcategoryBySlugOrId(params.categoryId, params.subcategoryId);
    // Checked before any redirect: an unknown slug is a 404 under every category, including one with an
    // inline product listing below.
    if (!page) {
      throw notFound();
    }

    // Categories with an inline product listing (e.g. "Ovládání a hlášení přes mobil") keep
    // subcategoryPages purely as internal grouping for that listing — they were never meant to be
    // browsable pages of their own (3_0, 4_0, 6_0), so route straight to the category page instead of rendering one.
    const categoryPage = await getCategoryPage(params.categoryId);
    if (categoryPage?.showInlineProductListing) {
      throw redirect({
        to: '/categories/$categoryId',
        params: { categoryId: category?.slug ?? params.categoryId },
        replace: true,
      });
    }

    const canonicalCategorySlug = category?.slug ?? page.categoryId;
    const canonicalSubSlug = page.slug || page.id;

    if (canonicalCategorySlug !== params.categoryId || canonicalSubSlug !== params.subcategoryId) {
      throw redirect({
        to: '/categories/$categoryId/$subcategoryId',
        params: {
          categoryId: canonicalCategorySlug,
          subcategoryId: canonicalSubSlug,
        },
        search: location.search,
        replace: true,
      });
    }

    return subcategoryPageData(catalog, page);
  },
  component: SubcategoryDetailRoute,
});

function SubcategoryDetailRoute() {
  const data = Route.useLoaderData();
  const { sub, q, connection, location, color, panel, availability, attrs } = Route.useSearch();

  return (
    <SubcategoryDetailPage
      data={data}
      activeSub={sub}
      q={q}
      connection={connection}
      location={location}
      color={color}
      panel={panel}
      availability={availability}
      attrs={attrs}
    />
  );
}
